import req from '../../api/request'
import { ENDPOINTS } from '../../api/endpoints'

/** The schedule level being queried — must match the id being passed. */
export type StandingsGroupBy = 'Division' | 'Conference'

/**
 * Fetches standings for a schedule. Called with a DIVISION's scheduleId the
 * response is that division's teams; with a CONFERENCE's id and
 * `groupBy: 'Conference'` it is that conference's. The raw payload is handed
 * to `toStandingsRows`, which owns the shape mapping and the ordering.
 */
export const getScheduleStandings = async (
  scheduleId: string,
  groupBy: StandingsGroupBy = 'Division'
): Promise<unknown> => {
  const { data } = await req(
    ENDPOINTS.SCHEDULES.GET_STANDINGS(scheduleId, groupBy)
  )
  return data
}

/** Query for the Schedule page's Standings tab — every field is optional. */
export interface StandingsListQuery {
  /** Name of the competition level to group by ("Division", "Pool", …). */
  groupBy?: string
  /** Stat id (or legacy field name) the rows are ordered by. */
  sortBy?: string | number
  sort?: 'asc' | 'desc'
  page?: number
}

/**
 * Fetches standings with a caller-chosen grouping and sort, for the Schedule
 * page's Standings tab. `getScheduleStandings` above stays on the Operations
 * panel's fixed points sort; this one passes the user's column sort through.
 * Returns the raw rows, each carrying its own `schedule` (the group it belongs
 * to) — `groupStandingsBySchedule` owns the grouping.
 */
export const getScheduleStandingsList = async (
  scheduleId: string,
  query: StandingsListQuery
): Promise<StandingsListRow[]> => {
  const { data } = await req(`/schedules/${scheduleId}/standings`, { query })
  return (data ?? []) as StandingsListRow[]
}

/** The fields of a standings row this client reads to group it. */
export interface StandingsListRow {
  schedule: { id: string; name: string }
  team: object | null
  [field: string]: unknown
}
